/**
 * One submission: the brief, the flags, and what to do before the meeting.
 *
 * The pre-submission transcript is deliberately not here. "The brief, not a
 * transcript" is the promise the intake makes to the expert, and showing the
 * conversation would quietly break it. What does appear is the tail from the
 * first follow-up onward, because those questions are the designer's own.
 */

import { useCallback, useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link } from '../router';
import { FlagCard } from '../components/FlagCard';
import { ShareLink } from '../components/ShareLink';
import { forWire, InterviewSettingsFields } from '../components/InterviewSettingsFields';
import { api, ApiError, type DetailFlag, type ProjectDetail } from '../api';
import {
  RADIUS,
  SECTIONS,
  display,
  firstName,
  formatDate,
  formatMeeting,
  mono,
  monoPlain,
  monoSmall,
  panel
} from '../theme';

export function Submission({ projectId }: { projectId: string }) {
  const { Badge, Button, Icon } = ds();
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [sent, setSent] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await api.projects.detail(projectId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that project.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (questionId: string, checked: boolean) => {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            flags: prev.flags.map((f) => ({
              ...f,
              questions: f.questions.map((q) => (q.id === questionId ? { ...q, checked } : q))
            }))
          }
        : prev
    );
    await api.questions.setChecked(projectId, questionId, checked).catch(() => void load());
  };

  const add = async (text: string, flagId: string | null) => {
    await api.questions.add(projectId, text, flagId).catch(() => {});
    await load();
  };

  const send = async () => {
    try {
      const res = await api.questions.send(projectId);
      setSent(res.sent);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Those did not send.');
    }
  };

  if (error) {
    return (
      <Frame>
        <p style={{ fontSize: 15, color: 'var(--danger-500)' }}>{error}</p>
        <Link to="/">
          <Button variant="ghost" size="md">
            All submissions
          </Button>
        </Link>
      </Frame>
    );
  }

  if (!detail) {
    return (
      <Frame>
        <p style={mono}>Loading</p>
      </Frame>
    );
  }

  const { project, conversation, brief, flags, stats, link, followupTail } = detail;
  const smeFirst = firstName(conversation?.smeName ?? project.smeName);
  const selected = flags.flatMap((f) => f.questions).filter((q) => q.checked && q.sentAt === null).length;

  return (
    <Frame>
      <Link to="/">
        <div style={{ ...monoPlain, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* A Lucide glyph, not a unicode arrow: the design system's
              iconography rule allows `·` and `—` and nothing else. */}
          <Icon name="arrow-left" size={13} />
          All submissions
        </div>
      </Link>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 32,
          flexWrap: 'wrap',
          marginBottom: 14
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            {conversation?.state === 'submitted' && (
              <Badge tone="signal" dot>
                New
              </Badge>
            )}
            {project.meetingAt && (
              <span style={mono}>Scope meeting · {formatMeeting(project.meetingAt)}</span>
            )}
          </div>
          <h1 style={{ ...display(44), textWrap: 'pretty' }}>{project.title}</h1>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {conversation?.submittedAt && (
            <Button
              variant="secondary"
              size="md"
              onClick={() => window.location.assign(`/api/scope/projects/${project.id}/export.md`)}
            >
              Export brief
            </Button>
          )}
          <Button variant="secondary" size="md" onClick={() => setSharing(true)}>
            Share link
          </Button>
          <Button variant="primary" size="md" disabled={selected === 0} onClick={() => void send()}>
            {selected > 0 ? `Send ${selected} to ${smeFirst}` : `Send to ${smeFirst}`}
          </Button>
        </div>
      </div>

      <div style={{ ...monoPlain, marginBottom: 24 }}>
        {conversation?.smeName ?? project.smeName}
        {project.team ? ` · ${project.team}` : ''}
        {conversation?.submittedAt ? ` · submitted ${formatDate(conversation.submittedAt)}` : ''}
        {stats ? ` · ${stats.questions} questions · ${stats.minutes} min` : ''}
        {link ? ` · link opened ${link.viewCount}×` : ''}
      </div>

      {sent !== null && (
        <div
          style={{
            ...panel,
            padding: '14px 18px',
            marginBottom: 20,
            fontSize: 14,
            color: 'var(--text-body)'
          }}
        >
          Sent {sent} to {smeFirst}.
        </div>
      )}

      {!conversation && (
        <div style={{ ...panel, padding: '40px 44px', maxWidth: 620 }}>
          <h2 style={display(26)}>Waiting on {smeFirst}.</h2>
          <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: 'var(--text-muted)' }}>
            Send them the intake link and the brief writes itself as they answer.
          </p>
        </div>
      )}

      {conversation && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ ...panel, padding: '36px 40px 34px', minWidth: 0, flex: '1 1 520px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                marginBottom: 26
              }}
            >
              <div>
                <div style={{ ...monoSmall, marginBottom: 8 }}>Written from the intake</div>
                <h2 style={display(28)}>Scoping brief</h2>
              </div>
              {conversation.state === 'active' && <Badge tone="neutral">In progress</Badge>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {SECTIONS.map((section) => {
                const paras = brief?.sections[section.key] ?? [];
                return (
                  <div
                    key={section.key}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '10px 24px',
                      padding: '20px 0',
                      borderTop: '1px solid var(--border-subtle)'
                    }}
                  >
                    <div style={{ ...monoSmall, flex: '0 0 168px', paddingTop: 3 }}>
                      {section.label}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, flex: '1 1 260px' }}>
                      {paras.length ? (
                        paras.map((text, i) => (
                          <p
                            key={i}
                            style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: 'var(--text-body)', textWrap: 'pretty' }}
                          >
                            {text}
                          </p>
                        ))
                      ) : (
                        <p style={{ margin: 0, fontSize: 15, color: 'var(--text-quiet)' }}>—</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {conversation.note && (
              <div
                style={{
                  marginTop: 28,
                  padding: '20px 22px',
                  background: 'var(--surface-inset)',
                  borderRadius: RADIUS.card
                }}
              >
                <div style={{ ...monoSmall, color: 'var(--accent)', marginBottom: 8 }}>
                  How the conversation went
                </div>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--text-body)', textWrap: 'pretty' }}>
                  {conversation.note}
                </p>
              </div>
            )}

            {followupTail.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <div style={{ ...monoSmall, marginBottom: 12 }}>Since the brief</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {followupTail.map((m) => (
                    <div
                      key={m.id}
                      style={{
                        padding: '12px 16px',
                        borderRadius: RADIUS.card,
                        background: m.role === 'sme' ? 'var(--surface-inset)' : 'transparent',
                        border: m.role === 'sme' ? 'none' : '1px solid var(--border-subtle)'
                      }}
                    >
                      <div style={{ ...monoSmall, marginBottom: 6 }}>
                        {m.role === 'sme' ? smeFirst : m.kind === 'followup' ? 'Asked' : 'Scope'}
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ ...panel, padding: '32px 32px 26px', minWidth: 0, flex: '1 1 380px', maxWidth: 428 }}>
            <div style={{ ...monoSmall, marginBottom: 8 }}>Before you meet</div>
            <h2 style={{ ...display(28), marginBottom: 6 }}>{pickUpHeading(flags)}</h2>
            <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)', textWrap: 'pretty' }}>
              Tick anything you would rather ask {smeFirst} first. The rest stays on the agenda.
            </p>

            {flags.length === 0 ? (
              <p style={{ margin: '16px 0 0', fontSize: 14, color: 'var(--text-faint)' }}>
                Nothing flagged. Add a question if there is something you want to ask first.
              </p>
            ) : (
              flags.map((flag, i) => (
                <FlagCard key={flag.id ?? `loose-${i}`} flag={flag} onToggle={toggle} onAdd={add} />
              ))
            )}
          </div>
        </div>
      )}

      {/* The same two-column split as the row above, so the settings panel is
          the width of the brief beside it rather than a third size on the
          page. The right-hand cell is empty on purpose — it reserves the
          column the flags occupy, which is what keeps the edges lined up. */}
      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start', marginTop: 20 }}
      >
        <Settings project={project} onSaved={load} />
        <div aria-hidden style={{ flex: '1 1 380px', maxWidth: 428 }} />
      </div>

      {sharing && (
        <ShareLink projectId={project.id} onClose={() => setSharing(false)} onRotated={load} />
      )}
    </Frame>
  );
}

/**
 * The rail's heading counts what is actually there.
 *
 * The mockup says "Three things to pick up", and the three are flags the
 * assistant raised. Counting flag *groups* would say "One thing to pick up"
 * over a card holding nothing but the designer's own questions, which is both
 * wrong and slightly baffling — so the untagged group is not a thing picked up.
 */
const WORDS = ['Nothing', 'One thing', 'Two things', 'Three things', 'Four things', 'Five things'];

function pickUpHeading(flags: DetailFlag[]): string {
  const raised = flags.filter((f) => f.tag !== null).length;
  // Not "Before the meeting" — that sits directly under the eyebrow "Before
  // you meet" and above a line ending "before the meeting", which said one
  // thing three times.
  if (raised === 0) return 'Nothing flagged.';
  return `${WORDS[raised] ?? `${raised} things`} to pick up`;
}

/** Per-project settings. The mockup put a Settings item in the nav; the only
 *  settings that exist belong to one project, so they live here. */
function Settings({
  project,
  onSaved
}: {
  project: ProjectDetail['project'];
  onSaved: () => Promise<void>;
}) {
  const { Button, Switch } = ds();
  const [notify, setNotify] = useState(project.notifyEmail);
  const [interview, setInterview] = useState(project.interview);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const saveInterview = async () => {
    setSaving(true);
    const next = forWire(interview);
    try {
      await api.projects.settings(project.id, next);
      setInterview(next);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...panel, padding: '28px 32px', minWidth: 0, flex: '1 1 520px' }}>
      <div style={{ ...monoSmall, marginBottom: 14 }}>Settings</div>
      <Switch
        label="Email me when a brief arrives"
        checked={notify}
        // The DS calls onChange with the next value first, not a change event.
        onChange={(next) => {
          setNotify(next);
          void api.projects.settings(project.id, { notifyEmail: next });
        }}
      />

      {/* The switch above saves on change; text fields cannot, so this half
          has a button. It stays available after a brief arrives — a forwarded
          link can start a second conversation. */}
      <div
        style={{ marginTop: 26, paddingTop: 24, borderTop: '1px solid var(--border-subtle)' }}
      >
        <div style={{ ...monoSmall, marginBottom: 18 }}>How the interview runs</div>
        <InterviewSettingsFields
          value={interview}
          onChange={(next) => {
            setInterview(next);
            setSaved(false);
          }}
          disabled={saving}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22 }}>
          <Button variant="secondary" size="sm" disabled={saving} onClick={() => void saveInterview()}>
            Save
          </Button>
          {saved && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Saved.</span>}
        </div>
      </div>

      <div style={{ marginTop: 26, paddingTop: 24, borderTop: '1px solid var(--border-subtle)' }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void api.projects
              .settings(project.id, { state: project.state === 'archived' ? 'open' : 'archived' })
              .then(onSaved);
          }}
        >
          {project.state === 'archived' ? 'Reopen project' : 'Archive project'}
        </Button>
      </div>
    </div>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 48px 140px' }}>{children}</div>;
}
